/**
 * Scaled-down representation of the real Kairosa dashboard rendered on top of
 * an arbitrary background theme + optional custom wallpaper URL. Used inside a
 * HoverCard on the branding page so owners see exactly what a theme will look
 * like across the whole app *before* committing to it.
 *
 * Deliberately pure CSS — no chart library, no real data — so it can render
 * many previews on one page (hover states, thumbnails) without cost.
 */
export function DashboardMiniPreview({ themeId, wallpaperUrl, overlayPct = 55 }) {
  const alpha = Math.max(0, Math.min(90, overlayPct)) / 100;
  const bg = backgroundStyle(themeId, wallpaperUrl, alpha);
  return (
    <div
      className="rounded-lg overflow-hidden border border-border shadow-xl"
      style={{ width: 280, height: 170 }}
      aria-label="Theme preview"
    >
      <div className="relative w-full h-full text-[6px] leading-none" style={bg}>
        {/* Sidebar */}
        <div className="absolute left-0 top-0 bottom-0 w-14 bg-card/85 backdrop-blur-sm border-r border-border/60 p-1.5 flex flex-col gap-1">
          <div className="h-2 w-8 rounded bg-primary/80" />
          <div className="mt-1 space-y-0.5">
            {["Dashboard","Fitness","Café","Marketing","Studio"].map((l) => (
              <div key={l} className="text-[5px] uppercase tracking-widest text-muted-foreground leading-tight py-0.5">{l}</div>
            ))}
          </div>
        </div>
        {/* Header + content */}
        <div className="absolute left-14 right-0 top-0 h-4 bg-card/60 border-b border-border/40 flex items-center px-1.5 gap-1">
          <div className="ml-auto h-1.5 w-6 rounded-full bg-muted" />
          <div className="h-1.5 w-1.5 rounded-full bg-primary" />
        </div>
        <div className="absolute left-16 right-2 top-6 space-y-1.5">
          <div className="flex gap-1">
            {[0,1,2,3].map((k) => (
              <div key={k} className="flex-1 rounded bg-card/80 border border-border/50 p-1">
                <div className="h-0.5 w-4 rounded bg-muted-foreground/60 mb-0.5" />
                <div className="h-1.5 w-8 rounded bg-primary/80" />
              </div>
            ))}
          </div>
          <div className="rounded bg-card/80 border border-border/50 p-1.5 h-16 flex items-end gap-0.5">
            {[24,36,48,42,60,56,72,68,80,74,58,50].map((h, i) => (
              <div key={i} className="flex-1 rounded-t bg-primary/70" style={{ height: `${h}%` }} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Returns the CSS `style` object for a given theme so the mini preview matches
 * exactly what the real body[data-bg-theme=...] rule produces in index.css.
 */
function backgroundStyle(themeId, wallpaperUrl, overlayAlpha) {
  const primary = "hsl(var(--primary))";
  const primaryA = (a) => `hsl(var(--primary) / ${a})`;
  const accentA = (a) => `hsl(var(--accent) / ${a})`;
  const bg = "hsl(var(--background))";
  const styles = {
    none: { backgroundColor: bg },
    aurora: {
      background: `radial-gradient(circle at 15% 20%, ${primaryA(0.28)}, transparent 45%), radial-gradient(circle at 85% 75%, ${accentA(0.22)}, transparent 45%), ${bg}`,
    },
    mesh: {
      background: `radial-gradient(at 15% 15%, ${primaryA(0.32)}, transparent 45%), radial-gradient(at 85% 25%, ${accentA(0.26)}, transparent 45%), radial-gradient(at 50% 100%, ${primaryA(0.22)}, transparent 55%), ${bg}`,
    },
    grid: {
      backgroundColor: bg,
      backgroundImage: `linear-gradient(${primaryA(0.10)} 1px, transparent 1px), linear-gradient(90deg, ${primaryA(0.10)} 1px, transparent 1px)`,
      backgroundSize: "16px 16px, 16px 16px",
    },
    dots: {
      backgroundColor: bg,
      backgroundImage: `radial-gradient(${primaryA(0.22)} 1px, transparent 1px)`,
      backgroundSize: "12px 12px",
    },
    waves: {
      backgroundColor: bg,
      backgroundImage: `repeating-linear-gradient(-45deg, ${primaryA(0.045)} 0 10px, transparent 10px 20px)`,
    },
    topography: {
      backgroundColor: bg,
      backgroundImage: `repeating-radial-gradient(circle at 20% 30%, ${primaryA(0.09)} 0 1.5px, transparent 1.5px 14px), repeating-radial-gradient(circle at 80% 70%, ${accentA(0.07)} 0 1.5px, transparent 1.5px 18px)`,
    },
    noise: { backgroundColor: bg },
    sunset: {
      background: `linear-gradient(180deg, ${bg} 0%, ${primaryA(0.14)} 60%, ${accentA(0.12)} 100%)`,
    },
    custom: wallpaperUrl
      ? {
          backgroundColor: bg,
          backgroundImage: `linear-gradient(hsl(var(--background) / ${overlayAlpha.toFixed(2)}), hsl(var(--background) / ${overlayAlpha.toFixed(2)})), url("${wallpaperUrl}")`,
          backgroundSize: "cover, cover",
          backgroundPosition: "center, center",
        }
      : { backgroundColor: bg },
  };
  return styles[themeId] || styles.none;
}
